import React, { useEffect, useState } from "react";
import Card from "components/card";
import { MdCheckCircle, MdCancel, MdError } from "react-icons/md";
import { useDevices, Device } from "context/DeviceContext";
import { useSelectedDevice } from "context/SelectedDeviceContext";
import { useAuth } from "context/AuthContext";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "services/firebase";

interface ResumoDispositivosProps {
  selectedUnidadeId: string | null;
}

export default function ResumoDispositivos({ selectedUnidadeId }: ResumoDispositivosProps) {
  const { devices } = useDevices();
  const { selectedDeviceId } = useSelectedDevice();
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const verificarDispositivos = async () => {
      if (!user?.uid || devices.length === 0) return;

      console.log("Executando verificação de status dos dispositivos...");

      for (const device of devices) {
        const agora = new Date();
        const diaSemanaAtual = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][agora.getDay()];
        const horaAtualStr = agora.toTimeString().slice(0, 5);

        let deveriaEstarOperando = false;
        if (device.days.includes(diaSemanaAtual) && device.expedientes) {
          for (const horario of Object.values(device.expedientes)) {
            if (horario.start && horario.end && horaAtualStr >= horario.start && horaAtualStr <= horario.end) {
              deveriaEstarOperando = true;
              break;
            }
          }
        }

        if (deveriaEstarOperando && device.status !== "Operando") {
          console.log(`Dispositivo "${device.name}" apresenta divergência de status.`);

          const notificationRef = doc(db, `clientes/${user.uid}/notifications/switch6`);

          try {
            const notificationDoc = await getDoc(notificationRef);

            if (notificationDoc.exists() && notificationDoc.data()?.checked === true && notificationDoc.data()?.notified !== true) {
              console.log(`Disparando notificação para o dispositivo "${device.name}".`);
              await setDoc(notificationRef, { notified: true }, { merge: true });
            } else {
              console.log(`Notificação para "${device.name}" não enviada (usuário desativou, já notificado ou config não existe).`);
            }
          } catch (error) {
            console.error("Erro ao verificar ou atualizar notificação:", error);
          }
        }
      }
    };

    const CINCO_MINUTOS_EM_MS = 5 * 60 * 1000;
    const intervalId = setInterval(verificarDispositivos, CINCO_MINUTOS_EM_MS);

    return () => clearInterval(intervalId);

  }, [devices, user]);

  const dispositivosFiltrados = selectedDeviceId
    ? devices.filter(d => d.id === selectedDeviceId)
    : devices;

  const statusIcon = (status: string) => {
    switch (status) {
      case "Operando":
        return <MdCheckCircle className="text-green-500" />;
      case "Desligado":
        return <MdCancel className="text-gray-500" />;
      case "Aguardando":
        return <MdError className="text-yellow-500" />;
      case "Erro":
        return <MdCancel className="text-red-500" />;
      default:
        return null;
    }
  };

  const getExpedienteAtual = (device: Device): { nome: string; progresso: number } | null => {
    const now = currentTime;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const currentDay = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][now.getDay()];

    if (!device.days.includes(currentDay)) {
      return null;
    }

    const parseTime = (time: string) => {
      if (!time) return 0;
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    };

    for (const [nome, horario] of Object.entries(device.expedientes || {})) {
      if (horario.start && horario.end) {
        const startMinutes = parseTime(horario.start);
        const endMinutes = parseTime(horario.end);

        let isRunning = false;
        let duracaoTotal = 0;
        let tempoPassado = 0;

        if (startMinutes < endMinutes) {
          // Diurno
          isRunning = currentMinutes >= startMinutes && currentMinutes <= endMinutes;
          duracaoTotal = endMinutes - startMinutes;
          tempoPassado = currentMinutes - startMinutes;
        } else {
          // Noturno (Madrugada)
          isRunning = currentMinutes >= startMinutes || currentMinutes <= endMinutes;
          duracaoTotal = (1440 - startMinutes) + endMinutes;
          tempoPassado = currentMinutes >= startMinutes
            ? currentMinutes - startMinutes
            : (1440 - startMinutes) + currentMinutes;
        }

        if (isRunning) {
          if (duracaoTotal <= 0) return { nome, progresso: 0 };
          const progresso = Math.round((tempoPassado / duracaoTotal) * 100);
          return { nome, progresso };
        }
      }
    }

    return null;
  };

  const formatarData = (date: Date) => {
    return date.toLocaleDateString("pt-BR");
  };

  return (
    <Card extra="w-full p-4 h-full">
      <h4 className="text-xl font-bold text-navy-700 mb-4">
        Resumo Dispositivos
      </h4>

      {dispositivosFiltrados.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          {selectedDeviceId
            ? "Nenhum dispositivo encontrado para o filtro atual."
            : "Nenhum dispositivo encontrado."
          }
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[280px]">
          <table className="w-full text-left">
            <thead>
              <tr className="text-sm font-bold text-gray-600">
                <th>Dispositivo</th>
                <th>Status</th>
                <th>Data</th>
                <th>Expediente</th>
              </tr>
            </thead>
            <tbody>
              {dispositivosFiltrados.map((device) => {
                const expedienteAtual = getExpedienteAtual(device);
                const deveriaEstarOperando = !!expedienteAtual;
                const statusExibido = (deveriaEstarOperando && device.status !== "Operando")
                  ? "Erro"
                  : device.status;

                return (
                  <tr key={device.id} className="border-t hover:bg-gray-50 transition-colors">
                    <td className="text-sm font-bold text-navy-700 py-3">
                      {device.name}
                    </td>
                    <td className="text-sm font-bold text-navy-700">
                      <div className="flex items-center gap-1">
                        {statusIcon(statusExibido)} {statusExibido}
                      </div>
                    </td>
                    <td className="text-sm font-bold text-navy-700">
                      {formatarData(currentTime)}
                    </td>
                    <td className="text-sm font-bold text-navy-700">
                      {expedienteAtual ? (
                        <div className="w-full">
                          <div className="flex justify-between mb-1 text-xs">
                            <span>{expedienteAtual.nome}</span>
                            {/* <span>{expedienteAtual.progresso}%</span> */}
                          </div>
                          <div className="w-full bg-gray-200 h-2 rounded-full">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 bg-[#CF4949]`}
                              style={{ width: `${expedienteAtual.progresso}%` }}
                            ></div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm font-bold text-navy-700">Aguardando...</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
